import {Box, createStyles} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import ScratchFileChip from 'pipelines/files/ScratchFileChip';
import ScratchFilePaneContent, {QueryingScratchFile} from 'pipelines/files/ScratchFilePaneContent';
import ScratchFilePreviewModal from 'pipelines/files/ScratchFilePreviewModal';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import {ChipSize} from 'ui-library/chips/Chip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {SCRATCH_FILE_LIST_GAP, SCRATCH_FILE_WRAPPER_SIZE} from './DataFrameTableConstants';
import {scratchFileRendersAsChip} from './useCachedInlinePreviewCount';

export const STRUCTURE_FILE_ICON: BenchlingIconName = 'atom-simple';

function StructureFileChip({scratchFile}: {scratchFile: ScratchFileContent}): JSX.Element {
  const [opened, {open, close}] = useDisclosure(false);
  return (
    <>
      <ScratchFileChip
        scratchFile={scratchFile}
        size={ChipSize.TABLE_CELL}
        icon={STRUCTURE_FILE_ICON}
        onClick={open}
      />
      <ScratchFilePreviewModal scratchFile={scratchFile} opened={opened} onClose={close} />
    </>
  );
}

function InlineScratchFilePreview({scratchFile}: {scratchFile: ScratchFileContent}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Box className={classes.inlinePreviewWrapper}>
      <ScratchFilePaneContent scratchFile={scratchFile} />
    </Box>
  );
}

interface QueryingScratchFileTableCellProps {
  fileId: string;
}

/**
 * Renders a scratch file in a data frame table cell. Structure files render as a chip that opens a preview
 * modal, since mounting a 3D viewer per row is too expensive. Other files preview inline.
 */
export default function QueryingScratchFileTableCell({
  fileId,
}: QueryingScratchFileTableCellProps): JSX.Element {
  return (
    <QueryingScratchFile fileId={fileId}>
      {(scratchFile) =>
        scratchFileRendersAsChip(scratchFile) ? (
          <StructureFileChip scratchFile={scratchFile} />
        ) : (
          <InlineScratchFilePreview scratchFile={scratchFile} />
        )
      }
    </QueryingScratchFile>
  );
}

const useStyles = createStyles(() => ({
  inlinePreviewWrapper: {
    height: SCRATCH_FILE_WRAPPER_SIZE,
    width: SCRATCH_FILE_WRAPPER_SIZE,
    overflow: 'scroll',
    position: 'relative',
    boxSizing: 'border-box',
    '& + &': {
      marginTop: SCRATCH_FILE_LIST_GAP,
    },
  },
}));
